import { useQuery, useMutation } from "@tanstack/react-query";
import { v2Client, v2SearchClient, v2TeamClient } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";
import type { Channel, ChannelConfiguration, Season, Team, TeamPlayers } from "./types";

function authHeaders(): Record<string, string> {
  const token = getCurrentAuth()?.AccessToken;
  return token ? { Authorization: `bearer ${token}` } : {};
}

/**
 * Fetch the channel being edited, for step-1 and step-2 prefill. Skipped for a
 * not-yet-created channel (id "0").
 *
 * NOT CACHED, deliberately. Both steps seed their form state from this, and
 * step 2 autosaves on every toggle, so a cached copy is a copy of the channel
 * as it was before those saves. Under the 30s v2 default, leaving a step and
 * coming back re-seeded the form from that pre-save copy and the edits looked
 * lost.
 *
 * gcTime: 0 as well as staleTime: 0, so there is no retained copy to hand back
 * while the refetch is in flight. That matters beyond the flicker: step 1
 * blocks Next on isLoading to avoid submitting a stale configuration, and
 * isLoading is false whenever cached data exists.
 */
export function useChannel(channelId: string) {
  return useQuery({
    queryKey: ["my-channel", "channel", channelId],
    enabled: Boolean(channelId) && channelId !== "0",
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    queryFn: async ({ signal }) => {
      const res = await v2Client.get<{ data: Channel | null }>(
        APIRoutes.GetChannel.replace(":id", channelId),
        { headers: authHeaders(), signal }
      );
      return res.data?.data ?? null;
    },
  });
}

/** Persist the channel's Teams/Players/Events/All/Seasons/Name. Used for both
 *  the per-toggle Season/Team autosaves and the final step-2 submit. */
export function useUpdateChannelPreferences(channelId: string) {
  return useMutation({
    mutationFn: async (configure: ChannelConfiguration) => {
      const res = await v2Client.post(
        APIRoutes.UpdateChannel.replace(":id", channelId),
        { configure },
        { headers: authHeaders() }
      );
      return res.data?.data;
    },
  });
}

/** Create a brand-new channel (step-1 submit when no channel exists yet). */
export function useCreateChannel() {
  return useMutation({
    mutationFn: async (configure: ChannelConfiguration) => {
      const res = await v2Client.post(
        APIRoutes.CreateChannel,
        { configure },
        { headers: authHeaders() }
      );
      return res.data?.data;
    },
  });
}

export function useSeasons() {
  return useQuery({
    queryKey: ["my-channel", "seasons"],
    queryFn: async ({ signal }) => {
      const res = await v2TeamClient.get<{ data: Season[] }>(APIRoutes.GetSeasons, {
        headers: authHeaders(),
        signal,
      });
      return res.data?.data ?? [];
    },
  });
}

export function useAllTeams() {
  return useQuery({
    queryKey: ["my-channel", "teams"],
    queryFn: async ({ signal }) => {
      const res = await v2SearchClient.get<{ data: Team[] }>(APIRoutes.GetAllTeamsHTP, {
        headers: authHeaders(),
        signal,
      });
      return res.data?.data ?? [];
    },
  });
}

export function usePlayersByTeams(teamIds: (string | number)[]) {
  return useQuery({
    queryKey: ["my-channel", "players", teamIds.join(",")],
    enabled: teamIds.length > 0,
    queryFn: async ({ signal }) => {
      const res = await v2SearchClient.get<{ data: TeamPlayers[] }>(
        `${APIRoutes.GetAllPlayersHTP}?team_ids=${teamIds.join(",")}&limit=10`,
        { headers: authHeaders(), signal }
      );
      return res.data?.data ?? [];
    },
  });
}
